class UniversalAccordion extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;
    this.allowMultiple = this.hasAttribute('allow-multiple');
    this.animationDuration = parseInt(this.getAttribute('animation-duration')) || 300;
  }

  connectedCallback() {
    this.setupAccordion();
  }

  disconnectedCallback() {
    // No cleanup needed
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setupAccordion() {
    const allDetails = this.querySelectorAll('details');
    const detailsElements = Array.from(allDetails).filter((details) => {
      return details.closest('universal-accordion') === this;
    });

    detailsElements.forEach((details, index) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.accordion-content');
      const icon = details.querySelector('.accordion-icon');

      if (!summary || !content) return;

      // Assign unique ID if not already present
      if (!content.id) {
        content.id = `accordion-content-${index}`;
      }

      // Set ARIA attributes
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-controls', content.id);
      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');

      if (!details.hasAttribute('open')) {
        content.style.height = '0';
        content.style.overflow = 'hidden';
        if (icon) {
          icon.style.transform = 'rotate(0deg)';
        }
      } else if (icon) {
        icon.style.transform = 'rotate(180deg)';
      }

      // Prevent default details behavior and handle manually
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        
        if (this.isAnimating) return;
        
        const isOpen = details.hasAttribute('open');
        
        if (isOpen) {
          this.closeAccordion(details, content, icon, summary);
        } else {
          // Close all other accordions first if multiple not allowed
          if (!this.allowMultiple) {
            this.closeAllAccordions(details);
          }
          // Then open the clicked one
          this.openAccordion(details, content, icon, summary);
        }
      });
    });
  }

  closeAllAccordions(exceptThis) {
    const allDetails = Array.from(this.querySelectorAll('details')).filter((details) => {
      return details.closest('universal-accordion') === this;
    });

    allDetails.forEach((details) => {
      // Skip the one we're about to open and any that are already closed
      if (details === exceptThis || !details.hasAttribute('open')) return;
      
      const content = details.querySelector('.accordion-content');
      const icon = details.querySelector('.accordion-icon');
      const summary = details.querySelector('summary');
      
      // Close immediately without animation for better UX
      details.removeAttribute('open');
      
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
      }
      
      if (content) {
        content.style.transition = 'none';
        content.style.height = '0';
        content.style.opacity = '0';
        content.style.marginTop = '0';
        content.style.overflow = 'hidden';
        // Re-enable transitions after a frame
        requestAnimationFrame(() => {
          content.style.transition = `height ${this.animationDuration}ms ease, opacity ${this.animationDuration}ms ease, margin-top ${this.animationDuration}ms ease`;
        });
      }
      
      if (icon) {
        icon.style.transform = 'rotate(0deg)';
      }
    });
  }

  openAccordion(details, content, icon, summary) {
    this.isAnimating = true;
    
    // Set aria first
    summary.setAttribute('aria-expanded', 'true');
    
    // Open the details
    details.setAttribute('open', '');
    
    // Get the natural height
    const height = content.scrollHeight;
    
    // Set height to 0 to start animation
    content.style.height = '0';
    content.style.overflow = 'hidden';
    
    // Force reflow
    content.offsetHeight;
    
    // Animate to natural height
    content.style.transition = `height ${this.animationDuration}ms ease, opacity ${this.animationDuration}ms ease, margin-top ${this.animationDuration}ms ease`;
    content.style.height = height + 'px';
    content.style.opacity = '1';
    
    // Rotate icon
    if (icon) {
      icon.style.transform = 'rotate(180deg)';
    }
    
    // After animation, set height to auto for responsiveness
    setTimeout(() => {
      content.style.height = 'auto';
      content.style.overflow = 'visible';
      this.isAnimating = false;
    }, this.animationDuration);
  }

  closeAccordion(details, content, icon, summary) {
    this.isAnimating = true;
    
    // Set aria first
    summary.setAttribute('aria-expanded', 'false');
    
    // Get current height
    const height = content.scrollHeight;
    content.style.height = height + 'px';
    content.style.overflow = 'hidden';
    
    // Force reflow
    content.offsetHeight;
    
    // Animate to 0
    content.style.transition = `height ${this.animationDuration}ms ease, opacity ${this.animationDuration}ms ease, margin-top ${this.animationDuration}ms ease`;
    content.style.height = '0';
    content.style.opacity = '0';
    content.style.marginTop = '0';
    
    // Rotate icon back
    if (icon) {
      icon.style.transform = 'rotate(0deg)';
    }
    
    // After animation, remove open attribute
    setTimeout(() => {
      details.removeAttribute('open');
      this.isAnimating = false;
    }, this.animationDuration);
  }

  // No resize handling needed for product accordions
}

export default UniversalAccordion;
